import React, { useEffect, useState, useRef } from 'react';
import './ProjectModal.css';
import { updateProject, createProject } from '../../api/api';

const ProjectModal = ({
  project,
  onClose,
  isEditing: initialIsEditing,
  onSave,
  isAuthenticated,
}) => {
  const [isEditing, setIsEditing] = useState(!!initialIsEditing);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [formData, setFormData] = useState({
    title: project?.title || '',
    description: project?.description || '',
    shortTech: project?.shortTech || '',
    stack: project?.stack?.join(', ') || '',
    demoUrl: project?.demoUrl || '',
    githubUrl: project?.githubUrl || '',
    publishedAt: project?.publishedAt
      ? new Date(project.publishedAt).toISOString().split('T')[0]
      : '',
    author: project?.author || 'Mikołaj Melnyk',
    sortOrder: project?.sortOrder?.toString() || '0',
    isFeatured: project?.isFeatured || false,
    mainImage: null,
    images: [],
  });

  const [existingImages, setExistingImages] = useState(project?.images || []);
  const [removedImageIds, setRemovedImageIds] = useState([]);

  const mainImageRef = useRef(null);
  const galleryRef = useRef(null);

  // block scrolla strony podczas otwartego modalu
  useEffect(() => {
    document.body.classList.add('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, []);

  // Block scrolla przy otwartym lightboxie
  useEffect(() => {
    if (fullScreenImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [fullScreenImage]);

  const allImages = React.useMemo(
    () => [
      ...(project?.mainImageUrl
        ? [{ url: project.mainImageUrl, isMain: true }]
        : []),
      ...existingImages.map((img) => ({ url: img.url, id: img.id })),
    ],
    [project?.mainImageUrl, existingImages]
  );

  const closeFullScreen = React.useCallback(() => {
    setFullScreenImage(null);
    setCurrentImageIndex(0);
  }, []);

  const nextImage = React.useCallback(() => {
    const nextIndex = (currentImageIndex + 1) % allImages.length;
    setCurrentImageIndex(nextIndex);
    setFullScreenImage(allImages[nextIndex]?.url || null);
  }, [currentImageIndex, allImages]);

  const prevImage = React.useCallback(() => {
    const prevIndex =
      (currentImageIndex - 1 + allImages.length) % allImages.length;
    setCurrentImageIndex(prevIndex);
    setFullScreenImage(allImages[prevIndex]?.url || null);
  }, [currentImageIndex, allImages]);

  useEffect(() => {
    if (!fullScreenImage) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeFullScreen();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullScreenImage, nextImage, prevImage, closeFullScreen]);

  const openFullScreen = (url, index = 0) => {
    setFullScreenImage(url);
    setCurrentImageIndex(index);
  };

  const renderMultilineText = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, index) => <p key={index}>{line}</p>);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'file') {
      if (name === 'mainImage') {
        setFormData((prev) => ({ ...prev, mainImage: files?.[0] || null }));
      } else if (name === 'images') {
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images, ...Array.from(files || [])],
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const removeNewFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const removeExistingImage = (id) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== id));
    setRemovedImageIds((prev) => [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = new FormData();
    form.append('title', formData.title);
    form.append('description', formData.description);
    form.append('shortTech', formData.shortTech);
    form.append(
      'stack',
      JSON.stringify(
        formData.stack
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );
    form.append('demoUrl', formData.demoUrl);
    form.append('githubUrl', formData.githubUrl || '');
    form.append(
      'publishedAt',
      formData.publishedAt || new Date().toISOString()
    );
    form.append('author', formData.author);
    form.append('sortOrder', formData.sortOrder);
    form.append('isFeatured', formData.isFeatured);

    if (formData.mainImage) {
      form.append('mainImage', formData.mainImage);
    }

    formData.images.forEach((file) => {
      form.append('images', file);
    });

    if (removedImageIds.length > 0) {
      form.append('removeImageIds', JSON.stringify(removedImageIds));
    }

    try {
      let result;
      if (project?.id) {
        result = await updateProject(project.id, form);
      } else {
        result = await createProject(form);
      }

      onSave(result);
      setSaveSuccess(true);

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Błąd zapisu projektu:', err);
      alert('Nie udało się zapisać projektu. Spróbuj ponownie.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <>
      <div className="modal-overlay active" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="close-btn" onClick={onClose} aria-label="Zamknij">
            <i className="fas fa-times"></i>
          </button>

          {saveSuccess ? (
            <div className="success-message">
              <h2>Zapisano pomyślnie!</h2>
              <p>Modal zamknie się za chwilę...</p>
            </div>
          ) : isEditing ? (
            <form onSubmit={handleSubmit} className="project-form">
              <h2>{project ? 'Edytuj projekt' : 'Dodaj nowy projekt'}</h2>

              <div className="form-group">
                <label htmlFor="title">Tytuł *</label>
                <input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Opis</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={5}
                />
              </div>

              <div className="form-group">
                <label htmlFor="shortTech">Skrócony opis</label>
                <input
                  id="shortTech"
                  name="shortTech"
                  value={formData.shortTech}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="stack">Stack (oddziel przecinkami)</label>
                <input
                  id="stack"
                  name="stack"
                  value={formData.stack}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="githubUrl">GitHub</label>
                <input
                  id="githubUrl"
                  name="githubUrl"
                  type="url"
                  value={formData.githubUrl}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="demoUrl">Demo / Live</label>
                <input
                  id="demoUrl"
                  name="demoUrl"
                  type="url"
                  value={formData.demoUrl}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="publishedAt">Data publikacji</label>
                <input
                  id="publishedAt"
                  name="publishedAt"
                  type="date"
                  value={formData.publishedAt}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="sortOrder">Kolejność sortowania</label>
                <input
                  id="sortOrder"
                  name="sortOrder"
                  type="number"
                  value={formData.sortOrder}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleInputChange}
                  />
                  Wyróżniony projekt
                </label>
              </div>

              {/* Zdjęcie główne */}
              <div className="form-group">
                <label>Zdjęcie główne</label>
                <div
                  className="image-dropzone"
                  onClick={() => mainImageRef.current?.click()}
                >
                  {formData.mainImage ? (
                    <img
                      src={URL.createObjectURL(formData.mainImage)}
                      alt="Podgląd"
                    />
                  ) : project?.mainImageUrl ? (
                    <img src={project.mainImageUrl} alt="Aktualne główne" />
                  ) : (
                    <span>Kliknij lub przeciągnij zdjęcie</span>
                  )}
                </div>
                <input
                  type="file"
                  name="mainImage"
                  accept="image/*"
                  ref={mainImageRef}
                  onChange={handleInputChange}
                  hidden
                />
              </div>

              {/* Galeria w trybie edycji */}
              <div className="form-group">
                <label>Galeria (dodatkowe zdjęcia)</label>
                <div
                  className="image-dropzone"
                  onClick={() => galleryRef.current?.click()}
                >
                  <span>Kliknij lub przeciągnij (można wiele)</span>
                </div>
                <input
                  type="file"
                  name="images"
                  accept="image/*"
                  multiple
                  ref={galleryRef}
                  onChange={handleInputChange}
                  hidden
                />

                <div className="gallery-preview">
                  {existingImages.map((img) => (
                    <div key={img.id} className="preview-item">
                      <img src={img.url} alt="" />
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeExistingImage(img.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  {formData.images.map((file, idx) => (
                    <div key={`new-${idx}`} className="preview-item">
                      <img src={URL.createObjectURL(file)} alt="" />
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeNewFile(idx)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  Zapisz
                </button>
                <button type="button" className="cancel-btn" onClick={onClose}>
                  Anuluj
                </button>
              </div>
            </form>
          ) : (
            <>
              <h2>{project.title}</h2>

              {isAuthenticated && (
                <button
                  className="edit-btn"
                  onClick={() => setIsEditing(true)}
                  style={{ marginBottom: '20px', backgroundColor: '#de5f00' }}
                >
                  Edytuj
                </button>
              )}

              {project.mainImageUrl ? (
                <img
                  src={project.mainImageUrl}
                  alt={project.title}
                  className="project-image main-image"
                  onClick={() => openFullScreen(project.mainImageUrl, 0)}
                  style={{ cursor: 'pointer' }}
                />
              ) : (
                <div className="no-main-image">Brak zdjęcia głównego</div>
              )}

              <div className="project-details">
                {project.shortTech && (
                  <p>
                    <strong>Skrócony opis:</strong> {project.shortTech}
                  </p>
                )}

                {project.description && (
                  <div className="description">
                    <strong>Opis:</strong>
                    <br />
                    {renderMultilineText(project.description)}
                  </div>
                )}

                {project.stack?.length > 0 && (
                  <p>
                    <strong>Stack:</strong> {project.stack.join(' • ')}
                  </p>
                )}

                <p>
                  <strong>Data publikacji:</strong>{' '}
                  {formatDate(project.publishedAt)}
                </p>

                <p>
                  <strong>Autor:</strong> {project.author || 'Mikołaj Melnyk'}
                </p>

                <div className="links">
                  {project.githubUrl && (
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link github"
                    >
                      <i className="fab fa-github"></i> GitHub
                    </a>
                  )}

                  {project.demoUrl && (
                    <a
                      href={project.demoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link demo"
                    >
                      <i className="fas fa-external-link-alt"></i> Demo
                    </a>
                  )}
                </div>
              </div>

              {project.images?.length > 0 && (
                <div className="gallery-section">
                  <h3 className="gallery-title">Galeria projektu</h3>
                  <div className="gallery-grid">
                    {project.images
                      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                      .map((img, index) => (
                        <div key={img.id} className="gallery-item">
                          <img
                            src={img.url}
                            alt={`Zdjęcie ${img.sortOrder + 1}`}
                            className="gallery-image"
                            onClick={() => openFullScreen(img.url, index + 1)}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Pełnoekranowy lightbox – WYCIĄGNIĘTY POZA MODAL */}
      {fullScreenImage && (
        <div className="fullscreen-overlay" onClick={closeFullScreen}>
          <div
            className="fullscreen-image-container"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={fullScreenImage}
              alt="Powiększone zdjęcie"
              className="fullscreen-image"
            />
          </div>

          <button
            className="fullscreen-close"
            onClick={closeFullScreen}
            aria-label="Zamknij powiększenie"
          >
            ×
          </button>

          {allImages.length > 1 && (
            <>
              <button
                className="fullscreen-nav prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
              >
                ←
              </button>
              <button
                className="fullscreen-nav next"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
              >
                →
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ProjectModal;
